/**
 * The organisation's own user directory — Active Directory, Samba AD, iRedMail
 * or another LDAP server — connected to its sign-in realm (ADR-0062). ZitiMail
 * only reads it: people sign in with their directory password, which stays in
 * the directory. Every action calls one route and reloads; a refusal is shown
 * in the API's words.
 */

import { useContext, useId, useState } from 'react';
import type {
  DirectoryBody,
  DirectoryMailboxes,
  DirectoryPreset,
  DirectoryState,
  DirectoryReadCounts,
  DirectoryTest,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { AdminWhereContext } from './mailboxes';
import { ActionButton, Loaded, useLoad, when } from './ui';

/** What a read of the directory changed (`POST …/directory/sync`, and after a connect). */
type ReadCounts = DirectoryReadCounts;

type Connected = NonNullable<DirectoryState['directory']>;

const PRESETS: Array<[DirectoryPreset, string, string]> = [
  ['ad', 'Active Directory or Samba AD', 'CN=svc-zitimail,CN=Users,DC=example,DC=com'],
  ['iredmail', 'iRedMail (OpenLDAP)', 'cn=vmail,dc=example,dc=com'],
  ['ldap', 'Other LDAP', 'cn=reader,dc=example,dc=com'],
];

const presetName = (preset: DirectoryPreset) => PRESETS.find(([p]) => p === preset)?.[1] ?? preset;

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);

function describeRead(read: ReadCounts): string {
  return `${read.added} added, ${read.updated} updated, ${read.removed} removed, ${read.failed} failed.`;
}

/** The settings already held, as a body for a test with the stored password. */
function storedBody(directory: Connected): DirectoryBody {
  return {
    preset: directory.preset,
    connection_url: directory.connection_url,
    base_dn: directory.base_dn,
    bind_dn: directory.bind_dn,
    ...(directory.preset === 'ldap' ? { user_filter: directory.user_filter } : {}),
  };
}

export function DirectoryPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<DirectoryState>(`${base(slug)}/directory`, { signal }),
    `directory:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Directory (Active Directory / LDAP)</h3>
      <p className="adm-muted">
        Connect the organisation&apos;s Active Directory, Samba AD or other LDAP directory, and the
        people in it can sign in to ZitiMail and be given mailboxes from it.
      </p>
      <p className="adm-muted">
        ZitiMail only reads the directory. People sign in with their directory password; passwords
        stay in the directory.
      </p>
      <Loaded load={load}>
        {(state) => (
          <DirectoryBodyView slug={slug} active={active} state={state} onChange={reload} />
        )}
      </Loaded>
    </section>
  );
}

function DirectoryBodyView({
  slug,
  active,
  state,
  onChange,
}: {
  slug: string;
  active: boolean;
  state: DirectoryState;
  onChange: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tested, setTested] = useState<DirectoryTest | null>(null);
  const [read, setRead] = useState<ReadCounts | null>(null);
  const directory = state.directory;

  if (state.blocked === 'provider') {
    return (
      <p className="adm-muted" role="note">
        This organisation signs in through its own identity provider (the panel above). Disconnect
        it there first to connect a directory.
      </p>
    );
  }
  if (state.blocked === 'foreign') {
    return (
      <p className="adm-muted" role="note">
        A directory was set up for this organisation by Genworx outside this panel. Ask Genworx to
        change it.
      </p>
    );
  }

  const noticeLine = notice && (
    <p className="adm-ok" role="status">
      {notice}
    </p>
  );

  if (!directory || editing) {
    return (
      <>
        {noticeLine}
        {active ? (
          <DirectoryForm
            slug={slug}
            state={state}
            current={directory}
            onSaved={(sync) => {
              const verb = directory ? 'Saved.' : 'Connected.';
              setNotice(
                sync
                  ? directory
                    ? `${verb} Read the directory: ${describeRead(sync)}`
                    : `${verb} Found ${people(sync.added)}.`
                  : verb,
              );
              setEditing(false);
              setTested(null);
              setRead(null);
              onChange();
            }}
            onCancel={directory ? () => setEditing(false) : undefined}
          />
        ) : (
          <p className="adm-muted">No directory is connected.</p>
        )}
      </>
    );
  }

  const found = `${directory.people}${directory.people_capped ? ' or more' : ''}`;
  return (
    <>
      {noticeLine}
      <dl className="adm-facts">
        <dt>Kind</dt>
        <dd>{presetName(directory.preset)}</dd>
        <dt>Server</dt>
        <dd className="adm-mono">{directory.connection_url}</dd>
        <dt>Base DN</dt>
        <dd className="adm-mono">{directory.base_dn}</dd>
        <dt>Users searched under</dt>
        <dd className="adm-mono">{directory.users_dn}</dd>
        <dt>Bind DN</dt>
        <dd className="adm-mono">{directory.bind_dn}</dd>
        {directory.user_filter && (
          <>
            <dt>Extra filter</dt>
            <dd className="adm-mono">{directory.user_filter}</dd>
          </>
        )}
        <dt>People found</dt>
        <dd>
          {found}
          {directory.without_mailbox > 0 &&
            ` — ${directory.without_mailbox} of them ${
              directory.without_mailbox === 1 ? 'has' : 'have'
            } no mailbox yet`}
        </dd>
      </dl>
      <p className="adm-muted adm-small">
        Changed by {directory.changed_by} on {when(directory.updated_at)}.
      </p>
      {active && (
        <>
          <div className="adm-inline">
            <ActionButton
              tone="ghost"
              run={async () => {
                setTested(null);
                setTested(
                  await api<DirectoryTest>(`${base(slug)}/directory/test`, {
                    method: 'POST',
                    body: storedBody(directory),
                  }),
                );
              }}
            >
              Test connection
            </ActionButton>
            <ActionButton
              tone="ghost"
              run={async () => {
                setRead(null);
                setRead(await api<ReadCounts>(`${base(slug)}/directory/sync`, { method: 'POST' }));
                onChange();
              }}
            >
              Read the directory now
            </ActionButton>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setNotice(null);
                setEditing(true);
              }}
            >
              Change
            </button>
            <ActionButton
              tone="danger"
              confirm="Disconnect the directory? People who sign in with their directory password will no longer be able to sign in until a password is set for them here or the directory is connected again."
              run={async () => {
                await api<DirectoryState>(`${base(slug)}/directory/disconnect`, {
                  method: 'POST',
                });
                setNotice(null);
                setTested(null);
                setRead(null);
                onChange();
              }}
            >
              Disconnect
            </ActionButton>
          </div>
          {tested && <TestResult result={tested} />}
          {read && <p role="status">Read the directory: {describeRead(read)}</p>}
          <MailboxesForEveryone slug={slug} onDone={onChange} />
          <TestPersonSignIn />
        </>
      )}
    </>
  );
}

function TestResult({ result }: { result: DirectoryTest }) {
  const connection =
    result.connection === 'ok' ? 'Reached the server.' : 'Could not reach the server.';
  const bind =
    result.bind === 'ok'
      ? 'The bind DN and password were accepted.'
      : result.bind === 'failed'
        ? 'The bind DN and password were refused.'
        : 'The bind DN and password were not tried.';
  const ok = result.connection === 'ok' && result.bind === 'ok';
  return (
    <div role="status">
      <ul className="adm-list" aria-label="Connection test">
        <li>
          <span className={result.connection === 'ok' ? 'adm-ok' : 'adm-error'}>{connection}</span>
        </li>
        <li>
          <span
            className={
              result.bind === 'ok' ? 'adm-ok' : result.bind === 'failed' ? 'adm-error' : 'adm-muted'
            }
          >
            {bind}
          </span>
        </li>
        {result.message && (
          <li>
            <span className={ok ? undefined : 'adm-error'}>{result.message}</span>
          </li>
        )}
      </ul>
    </div>
  );
}

/** A dry run first, listing who would get one; then, once confirmed, the real run. */
function MailboxesForEveryone({ slug, onDone }: { slug: string; onDone: () => void }) {
  const { base } = useContext(AdminWhereContext);
  const [plan, setPlan] = useState<DirectoryMailboxes | null>(null);
  const [made, setMade] = useState<DirectoryMailboxes | null>(null);
  const run = (dryRun: boolean) =>
    api<DirectoryMailboxes>(`${base(slug)}/directory/mailboxes`, {
      method: 'POST',
      body: { dry_run: dryRun },
    });
  return (
    <div className="adm-sub">
      <h4>Mailboxes</h4>
      {!plan && (
        <div className="adm-inline">
          <ActionButton
            tone="ghost"
            run={async () => {
              setMade(null);
              setPlan(await run(true));
            }}
          >
            Create mailboxes for everyone in the directory
          </ActionButton>
        </div>
      )}
      {plan && (
        <>
          {plan.addresses.length === 0 ? (
            <p>Everyone in the directory on the organisation&apos;s domains has a mailbox.</p>
          ) : (
            <>
              <p>
                {plan.addresses.length === 1
                  ? '1 person would get a mailbox, with the default quota and a seat:'
                  : `${plan.addresses.length} people would get a mailbox, each with the default quota and a seat:`}
              </p>
              <ul className="adm-list" aria-label="Would get a mailbox">
                {plan.addresses.map((address) => (
                  <li key={address} className="adm-mono">
                    {address}
                  </li>
                ))}
              </ul>
            </>
          )}
          <MailboxProblems result={plan} />
          <div className="adm-inline">
            {plan.addresses.length > 0 && (
              <ActionButton
                confirm={`Create ${
                  plan.addresses.length === 1 ? '1 mailbox' : `${plan.addresses.length} mailboxes`
                }? Each takes a seat.`}
                run={async () => {
                  const result = await run(false);
                  setPlan(null);
                  setMade(result);
                  onDone();
                }}
              >
                {plan.addresses.length === 1
                  ? 'Create 1 mailbox'
                  : `Create ${plan.addresses.length} mailboxes`}
              </ActionButton>
            )}
            <button type="button" className="btn ghost" onClick={() => setPlan(null)}>
              {plan.addresses.length > 0 ? 'Cancel' : 'Close'}
            </button>
          </div>
        </>
      )}
      {made && (
        <div role="status">
          <p>
            {made.addresses.length === 0
              ? 'No mailboxes were made.'
              : `Made ${made.addresses.length === 1 ? '1 mailbox' : `${made.addresses.length} mailboxes`}: ${made.addresses.join(', ')}.`}
          </p>
          <MailboxProblems result={made} />
        </div>
      )}
    </div>
  );
}

function MailboxProblems({ result }: { result: DirectoryMailboxes }) {
  return (
    <>
      {result.over_seats.length > 0 && (
        <p className="adm-error">
          No seats left for {people(result.over_seats.length)}: {result.over_seats.join(', ')}. Add
          seats, then run this again.
        </p>
      )}
      {result.failed.length > 0 && (
        <ul className="adm-list" aria-label="Could not be made">
          {result.failed.map((f) => (
            <li key={f.email}>
              <span className="adm-mono">{f.email}</span>:{' '}
              <span className="adm-error">{f.message}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function TestPersonSignIn() {
  const [address, setAddress] = useState('');
  const typed = address.trim();
  const target = `/auth/login?${typed ? `email=${encodeURIComponent(typed)}&` : ''}via=directory`;
  return (
    <div className="adm-sub">
      <h4>Test a person&apos;s sign-in</h4>
      <div className="adm-inline">
        <label>
          Their address
          <input
            type="email"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="jane@example.com"
          />
        </label>
        <a className="btn ghost" href={target} target="_blank" rel="noopener">
          Test sign-in
        </a>
      </div>
      <p className="adm-muted adm-small">
        Signs in as that person on the sign-in page; use a private window so your own session is not
        replaced.
      </p>
    </div>
  );
}

function DirectoryForm({
  slug,
  state,
  current,
  onSaved,
  onCancel,
}: {
  slug: string;
  state: DirectoryState;
  current: DirectoryState['directory'];
  onSaved: (sync: ReadCounts | undefined) => void;
  onCancel: (() => void) | undefined;
}) {
  const { base } = useContext(AdminWhereContext);
  const id = useId();
  const [preset, setPreset] = useState<DirectoryPreset>(current?.preset ?? 'ad');
  const [url, setUrl] = useState(current?.connection_url ?? '');
  const [baseDn, setBaseDn] = useState(current?.base_dn ?? state.suggested_base_dn ?? '');
  const [bindDn, setBindDn] = useState(current?.bind_dn ?? '');
  const [password, setPassword] = useState('');
  const [filter, setFilter] = useState(current?.user_filter ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState<DirectoryTest | null>(null);

  const body = (): DirectoryBody => ({
    preset,
    connection_url: url.trim(),
    base_dn: baseDn.trim(),
    bind_dn: bindDn.trim(),
    // Empty on a change keeps the password held; it is never shown back.
    ...(password ? { bind_password: password } : {}),
    ...(preset === 'ldap' ? { user_filter: filter.trim() || null } : {}),
  });
  const bindPlaceholder = PRESETS.find(([p]) => p === preset)?.[2] ?? '';

  return (
    <form
      className="adm-form"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setBusy(true);
        api<DirectoryState & { sync?: ReadCounts }>(`${base(slug)}/directory`, {
          method: 'POST',
          body: body(),
        })
          .then((saved) => onSaved(saved.sync))
          .catch((e: unknown) => {
            setError(describeError(e));
            setBusy(false);
          });
      }}
    >
      <fieldset className="adm-roles">
        <legend>Kind of directory</legend>
        {PRESETS.map(([value, label]) => (
          <label key={value}>
            <input
              type="radio"
              name={`${id}-preset`}
              value={value}
              checked={preset === value}
              onChange={() => setPreset(value)}
            />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="adm-fields">
        <label>
          Server
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="ldaps://ldap.example.com:636"
            maxLength={500}
            required
            spellCheck={false}
            aria-describedby={`${id}-server-help`}
          />
        </label>
      </div>
      <p id={`${id}-server-help`} className="adm-muted adm-small">
        {state.ldaps_required &&
          "The address must start with ldaps:// and the server's certificate must be trusted: a public certificate, such as one from Let's Encrypt. "}
        {state.internal_allowed
          ? 'As Genworx, you may use an internal address here, reached through ZitiNexus or a VPN.'
          : 'It must be an address reachable from the internet. For a directory on an internal network, Genworx can connect it through ZitiNexus or a VPN.'}
      </p>
      <div className="adm-fields">
        <label>
          Base DN
          <input
            value={baseDn}
            onChange={(e) => setBaseDn(e.target.value)}
            placeholder="dc=example,dc=com"
            maxLength={500}
            required
            spellCheck={false}
          />
        </label>
        <label>
          Bind DN
          <input
            value={bindDn}
            onChange={(e) => setBindDn(e.target.value)}
            placeholder={bindPlaceholder}
            maxLength={500}
            required
            spellCheck={false}
          />
        </label>
        <label>
          Bind password
          <input
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={current ? 'unchanged' : ''}
            required={!current}
          />
        </label>
        {preset === 'ldap' && (
          <label>
            Extra filter (optional)
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="(objectClass=inetOrgPerson)"
              maxLength={500}
              spellCheck={false}
            />
          </label>
        )}
      </div>
      <p className="adm-muted adm-small">
        The bind DN is an account that can read the directory; it needs no other rights. Its
        password is kept by the sign-in service only and is never shown again
        {current ? '; leave it empty to keep the one held' : ''}.
      </p>
      <div className="adm-inline">
        <ActionButton
          tone="ghost"
          run={async () => {
            setTested(null);
            setTested(
              await api<DirectoryTest>(`${base(slug)}/directory/test`, {
                method: 'POST',
                body: body(),
              }),
            );
          }}
        >
          Test connection
        </ActionButton>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Working…' : current ? 'Save changes' : 'Connect'}
        </button>
        {onCancel && (
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
      </div>
      {tested && <TestResult result={tested} />}
    </form>
  );
}
